/**
 * oRPC query utils whose cache keys are scoped to one signed-in user, or to
 * signed-out readers.
 *
 * A read that answers differently for different users lands on a separate
 * cache entry for each, so nothing one user loaded is served to the next.
 */
import { createRouterUtils, type RouterUtils } from '@orpc/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import type { ApiClient } from 'system/orpc';
import { useSession } from 'util/auth-client';

// TODO: Pass the scope as `prefix` instead of `path` once on oRPC 2.0.
export function createUserQueryUtils({ client, userId }: { client: ApiClient; userId: string | null }): RouterUtils<ApiClient> {
    return createRouterUtils(client, { path: ['user', userId ?? ''] });
}

/**
 * Query utils scoped to the signed-in user. When the user changes, the
 * previous user's reads are removed from the cache, and the cached mutations
 * are cleared.
 */
export function useUserQueryUtils({ apiClient, queryClient }: { apiClient: ApiClient; queryClient: QueryClient }): RouterUtils<ApiClient> {
    const userId = useSession().data?.user.id ?? null;
    const apiQueryUtils = useMemo(() => createUserQueryUtils({ client: apiClient, userId }), [apiClient, userId]);

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: apiQueryUtils.key() });
            queryClient.getMutationCache().clear();
        };
    }, [queryClient, apiQueryUtils]);

    return apiQueryUtils;
}
